import React from 'react';
import { FormTemplate, TextArea, TextInput } from 'ui-builder';
import {
  Market,
  SetupArea,
  SetupAreaVersionStatus,
  UpdateMarketRequest,
  useMarketsAdminService,
  useSetupAreasAdminService
} from 'nekst-api';
import { StateDropdown } from 'features/tasks-forms';
import { SetupAreaVersionStatusDropdown } from './SetupAreaVersionStatusDropdown';
import { Separator, TimezoneDropdown } from 'features/nekst-widgets';
import useFeatureToggleService from 'feature-toggle';

export function UpdateMarketForm(props: {
  afterSubmitFunc?: () => void,
  setupArea: SetupArea
}) {
  const marketsService = useMarketsAdminService();
  const setupAreaAdminService = useSetupAreasAdminService();

  const featureToggleService = useFeatureToggleService();

  const setupArea = props.setupArea;

  return (
    <FormTemplate<UpdateMarketRequest & {
      versionStatus: SetupAreaVersionStatus,
      versionName: string,
    }, Market>
      title="Update Market"
      submitFormFunc={async (data) => {
        const result = await marketsService.updateMarket(setupArea.market!.id, data);

        if (!featureToggleService.isSetupAreasVersioningEnabled()) {
          await setupAreaAdminService.setVersionStatus(
            setupArea.id,
            setupArea.versions[0].version,
            data.versionStatus
          );
          await setupAreaAdminService.setVersionName(
            setupArea.id,
            setupArea.versions[0].version,
            data.versionName
          );
        }

        if (props.afterSubmitFunc) {
          props.afterSubmitFunc();
        }
        return result;
      }}
      initialData={{
        ...setupArea.market,
        alias: setupArea.alias,
        timezone: setupArea.timezone,
        versionStatus: setupArea.versions[0].status,
        versionName: setupArea.versions[0].version
      }}
      validationSchema={{
        name: {
          type: 'string',
          constraints: {
            required: true
          }
        },
        alias: {
          type: 'string',
          constraints: !setupArea.alias ? {
            required: true,
            minLength: 3,
            maxLength: 30,
            regex: {
              value: /^[a-zA-Z0-9\\-]+$/,
              message: 'Alias must start with a letter and can only contain letters and numbers.'
            }
          } : {}
        },
        state: {
          type: 'string',
          constraints: {
            required: true
          }
        },
        ...featureToggleService.isSetupAreasVersioningEnabled() ? {} : {
          versionStatus: {
            type: 'string',
            constraints: {
              required: true
            }
          },
          versionName: {
            type: 'string',
            constraints: {
              required: true,
              minLength: 3,
              maxLength: 12,
              regex: {
                value: /^[a-zA-Z0-9\\-]+$/,
                message: 'Version name must start with a letter and can only contain letters and numbers.'
              }
            }
          }
        }
      }}
    >
      <TextInput source="name" label="Market Name" />
      <TextInput
        source="alias"
        label="Alias"
        hint={!setupArea.alias ? 'Market alias can be from 3 to 12 characters long, starting with a letter and containing only letters and numbers. When created, it cannot be changed.' : 'Alias is already set, cannot be changed.'}
      />
      <StateDropdown source="state" label="State" />
      <TimezoneDropdown source="timezone" label="Timezone" placeholder="Select timezone" />
      <TextArea source="description" label="Description"
                placeholder="Enter market description here..." />
      {!featureToggleService.isSetupAreasVersioningEnabled() && (
        <>
          <Separator />
          <SetupAreaVersionStatusDropdown source="versionStatus" label="Publishing Status" />
          <TextInput source="versionName" label="Version Name" />
        </>
      )}
    </FormTemplate>
  );
}
